/**
 * El aviso de que el bot todavía no está en vivo.
 *
 * Un dueño que se registra entra al panel y ve… un panel. Sin conversaciones,
 * sin métricas, sin nada — porque su bot todavía no atiende a nadie. Nada le
 * dice que le falta terminar, ni por dónde. Idea del product owner
 * (2026-09-03): un aviso en la pantalla inicial que lleve a la configuración.
 *
 * ## Tres decisiones
 *
 * **No se puede cerrar.** Si se pudiera, se cierra y no se termina nunca. No es
 * una notificación: es el estado de la cuenta.
 *
 * **Desaparece sola cuando el bot sale en vivo**, y eso lo dice la META del
 * tenant, no el estado del alta. Mirando sólo el alta, un tenant ya activo
 * seguiría leyendo «poné tu bot en vivo» para siempre.
 *
 * **Dice QUÉ falta.** Un aviso que no nombra el siguiente paso obliga a entrar
 * a buscarlo, y ahí se abandona. El texto sale de `derivarEstado`, la misma
 * derivación que decide el paso del alta, así que el aviso y la pantalla nunca
 * pueden discrepar.
 */

import type { RespuestaDeOnboarding } from '@chatbot/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.tsx';
import { api } from '../lib/api.ts';
import { type IdDePaso, derivarEstado } from '../pages/alta/EstadoDelAlta';

/**
 * Tres pasos desde GKTIBO-120: conectar un canal dejó de ser uno de ellos.
 *
 * El canal se avisa aparte, en la pantalla de cierre y en el indicador
 * discreto del panel, porque no bloquea terminar el alta — sólo salir en vivo.
 */
const QUE_FALTA: Record<IdDePaso, string> = {
  identidad: 'Falta terminar de crear tu negocio.',
  conocimiento:
    'Te falta contarle a tu bot sobre tu negocio —y probarlo— antes de que le escriba un cliente.',
  listo: 'Ya está todo listo. Sólo falta poner tu bot en vivo.',
};

export function AvisoDeConfiguracion() {
  const { user } = useAuth();
  // De `custom:tenantId`. Un operador nuestro no tiene ninguno: no tiene un
  // negocio propio que configurar y el aviso sería ruido en cada pantalla.
  const tenantId = user?.tenantId ?? undefined;

  const { data: elTenant } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => api.tenants.get(tenantId as string),
    enabled: Boolean(tenantId),
  });

  const yaEstaEnVivo = elTenant?.tenant?.status === 'active';

  const { data } = useQuery({
    queryKey: ['onboarding-status', tenantId],
    queryFn: () => api.tenants.getOnboardingStatus(tenantId as string),
    enabled: Boolean(tenantId),
  });

  if (!tenantId || yaEstaEnVivo || !data) return null;

  const estado = data as RespuestaDeOnboarding;
  const derivado = derivarEstado(estado);
  const paso = derivado.pasoActivo;
  const listo = estado.ready_to_activate;

  /**
   * Terminó el alta y lo único que falta es el canal: **no se insiste**.
   *
   * Decisión del PO (GKTIBO-120): se avisa una vez, en la pantalla de cierre,
   * con las dos salidas a la vista. Después queda esto y nada más. Un cartel
   * que vuelve en cada carga es lo que hace que la próxima vez que el producto
   * diga algo, nadie lo lea.
   *
   * Mientras el alta NO está terminada la cosa es distinta y el aviso grande
   * se queda: ahí sí es trabajo pendiente del dueño, y guiarlo es nuestro
   * trabajo.
   */
  if (paso === 'listo' && derivado.faltaCanal) {
    return (
      <Link
        to="/configuracion/canales"
        data-testid="indicador-sin-canal"
        className="mb-6 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium border-warning-500/30 bg-warning-500/10 text-content-secondary hover:opacity-90"
      >
        <span aria-hidden="true">!</span>
        Sin canal conectado
      </Link>
    );
  }

  return (
    <output
      data-testid="aviso-configuracion"
      className={`mb-6 flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between ${
        listo
          ? 'border-success-500/30 bg-success-500/10'
          : 'border-warning-500/30 bg-warning-500/10'
      }`}
    >
      <div className="flex min-w-0 items-start gap-3">
        <span aria-hidden="true" className="text-lg leading-none">
          {listo ? '✓' : '!'}
        </span>
        <div className="min-w-0">
          <p className="font-medium text-content-primary">
            {listo ? 'Tu bot está listo para salir en vivo' : 'Tu bot todavía no está atendiendo'}
          </p>
          <p className="mt-0.5 text-sm text-content-secondary">{QUE_FALTA[paso]}</p>
        </div>
      </div>
      <Link
        to="/alta"
        className="shrink-0 rounded-md bg-surface-brand-soft px-4 py-2 text-center text-sm font-medium text-content-brand hover:opacity-90"
      >
        {listo ? 'Poner mi bot en vivo' : 'Terminar de configurar'}
      </Link>
    </output>
  );
}
